import type { AppStore } from '../core/store';
import type { AppState, WorkScope } from '../core/types';
import { createLayerClassification, createMasterClassification } from '../domain/classification';
import { featureIdsForLayerScope } from '../domain/displaySet';
import { emptySelectionSet } from '../domain/selectionSet';
import { emptyWorkScope, normalizeWorkScope, workScopeSummary } from '../domain/scope';
import { idsForScope } from '../domain/themeTarget';

type ScopeKind = 'finca' | 'suerte' | 'tablon';

const esc = (value: string): string => value.replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char] ?? char);
const normalized = (value: string): string => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();

export class ScopePanel {
  private readonly queries: Record<ScopeKind, string> = { finca: '', suerte: '', tablon: '' };
  private rendered: { readonly model: AppState['model']; readonly workScope: AppState['workScope']; readonly queries: string } | null = null;

  constructor(private readonly element: HTMLElement, private readonly store: AppStore) {
    element.addEventListener('input', (event) => {
      const input = (event.target as Element).closest<HTMLInputElement>('[data-scope-search]');
      if (!input) return;
      this.queries[input.dataset.scopeSearch as ScopeKind] = normalized(input.value.trim());
      this.render(this.store.getState());
      input.focus();
    });
    element.addEventListener('change', (event) => {
      const input = (event.target as Element).closest<HTMLInputElement>('[data-scope-kind]');
      if (input) this.toggle(input.dataset.scopeKind as ScopeKind, input.value, input.checked);
    });
    element.addEventListener('click', (event) => {
      const target = event.target as Element;
      if (target.closest('#scope-clear')) { this.applyScope(emptyWorkScope()); return; }
      const chip = target.closest<HTMLButtonElement>('[data-scope-remove]');
      if (chip) this.toggle(chip.dataset.scopeRemove as ScopeKind, chip.value, false);
    });
  }

  /** Only model, work scope and the panel's own searches feed this view; selection or style updates skip it. */
  render(state: AppState): void {
    const queries = `${this.queries.finca}\0${this.queries.suerte}\0${this.queries.tablon}`;
    if (this.rendered?.model === state.model && this.rendered.workScope === state.workScope && this.rendered.queries === queries) return;
    this.rendered = { model: state.model, workScope: state.workScope, queries };
    const model = state.model;
    const scope = normalizeWorkScope(model, state.workScope);
    const fincaSet = new Set(scope.fincaIds); const suerteSet = new Set(scope.suerteIds); const tablonSet = new Set(scope.tablonIds);
    const fincas = model ? [...model.fincas.values()].sort((a, b) => a.name.localeCompare(b.name, 'es', { numeric: true })) : [];
    const suertes = model ? [...model.suertes.values()].filter((item) => fincaSet.has(item.fincaId)).sort((a, b) => {
      const fincaA = model.fincas.get(a.fincaId)?.name ?? ''; const fincaB = model.fincas.get(b.fincaId)?.name ?? '';
      return fincaA.localeCompare(fincaB, 'es', { numeric: true }) || a.label.localeCompare(b.label, 'es', { numeric: true });
    }) : [];
    const tablones = model ? [...model.tablones.values()].filter((item) => suerteSet.has(item.suerteId)).sort((a, b) => a.code.localeCompare(b.code, 'es', { numeric: true })) : [];

    this.renderOptions('finca', fincas.map((item) => ({ id: item.id, label: `${item.name} · ${item.code}`, search: `${item.name} ${item.code}` })), fincaSet, Boolean(model));
    this.renderOptions('suerte', suertes.map((item) => ({ id: item.id, label: `${model!.fincas.get(item.fincaId)?.name ?? ''} · Suerte ${item.label}`, search: `${model!.fincas.get(item.fincaId)?.name ?? ''} ${item.label} ${item.code}` })), suerteSet, Boolean(scope.fincaIds.length));
    this.renderOptions('tablon', tablones.map((item) => {
      const suerte = model!.suertes.get(item.suerteId); const finca = suerte ? model!.fincas.get(suerte.fincaId) : undefined;
      return { id: item.id, label: `${finca?.name ?? ''} · S${suerte?.label ?? ''} · T${item.code}`, search: `${finca?.name ?? ''} ${suerte?.label ?? ''} ${item.code}` };
    }), tablonSet, Boolean(scope.suerteIds.length));

    const summary = workScopeSummary(model, scope);
    this.element.querySelector<HTMLElement>('#scope-summary')!.textContent = model ? `${summary.label === 'Todo CASUR' ? 'Todo CASUR' : `Ámbito: ${summary.label}`} · ${summary.suerteCount.toLocaleString('es-NI')} suerte(s). Elige fincas, suertes o tablones para acotar el trabajo.` : 'Carga un Maestro CASUR.';
  }

  private renderOptions(kind: ScopeKind, options: readonly { id: string; label: string; search: string }[], selected: ReadonlySet<string>, enabled: boolean): void {
    const visible = options.filter((item) => !this.queries[kind] || normalized(item.search).includes(this.queries[kind]));
    const container = this.element.querySelector<HTMLElement>(`#scope-${kind}-options`)!;
    container.innerHTML = !enabled
      ? `<small>${kind === 'finca' ? 'Carga un Maestro CASUR.' : kind === 'suerte' ? 'Selecciona una o más fincas.' : 'Selecciona una o más suertes.'}</small>`
      : visible.map((item) => `<label><input type="checkbox" data-scope-kind="${kind}" value="${esc(item.id)}" ${selected.has(item.id) ? 'checked' : ''}/><span>${esc(item.label)}</span></label>`).join('') || '<small>Sin coincidencias.</small>';
    const chips = this.element.querySelector<HTMLElement>(`#scope-${kind}-chips`)!;
    const byId = new Map(options.map((item) => [item.id, item.label]));
    chips.innerHTML = [...selected].map((id) => `<button type="button" data-scope-remove="${kind}" value="${esc(id)}" title="Quitar">${esc(byId.get(id) ?? id)} ×</button>`).join('');
  }

  private toggle(kind: ScopeKind, id: string, checked: boolean): void {
    const current = this.store.getState().workScope;
    const key = `${kind}Ids` as 'fincaIds' | 'suerteIds' | 'tablonIds';
    const values = new Set(current[key]);
    checked ? values.add(id) : values.delete(id);
    let next: WorkScope = { ...current, [key]: [...values] };
    if (kind === 'finca') next = { ...next, suerteIds: next.suerteIds.filter((suerteId) => {
      const suerte = this.store.getState().model?.suertes.get(suerteId);
      return Boolean(suerte && next.fincaIds.includes(suerte.fincaId));
    }) };
    if (kind !== 'tablon') next = { ...next, tablonIds: next.tablonIds.filter((tablonId) => {
      const tablon = this.store.getState().model?.tablones.get(tablonId);
      return Boolean(tablon && next.suerteIds.includes(tablon.suerteId));
    }) };
    this.applyScope(next);
  }

  private applyScope(requested: WorkScope): void {
    const state = this.store.getState();
    const scope = normalizeWorkScope(state.model, requested);
    const classifiedLayer = state.layers.find(({ id }) => id === state.classificationConfig.layerId);
    const scopedIds = classifiedLayer ? idsForScope(classifiedLayer, state, scope) : new Set<string>();
    const classificationConfig = (state.classificationConfig.mode === 'single' || state.classificationConfig.field) && classifiedLayer && state.classificationConfig.themeTarget.source === 'scope'
      ? classifiedLayer.kind === 'master' && state.model
        ? createMasterClassification(state.model, { ...state.classificationConfig, themeTarget: { source: 'scope', scope, featureIds: [...scopedIds] } }, scopedIds, state.operationalFields)
        : createLayerClassification(classifiedLayer, { ...state.classificationConfig, themeTarget: { source: 'scope', scope, featureIds: [...scopedIds] } }, scopedIds)
      : state.classificationConfig;
    const selectedLayer = state.layers.find(({ id }) => id === state.selectionSet.layerId);
    const selectedLayerIds = selectedLayer && selectedLayer.kind !== 'master'
      ? featureIdsForLayerScope(selectedLayer, state.model, scope)
      : new Set<string>();
    const selectionOutsideScope = Boolean(selectedLayer && selectedLayer.kind !== 'master' && selectedLayer.scopeMode !== 'none' && state.selectionSet.entries.some((entry) => !selectedLayerIds.has(entry.id)));
    const clearSelection = state.selectionSet.entries.some((entry) => entry.source === 'master') || selectionOutsideScope;
    this.store.update({
      workScope: scope,
      activeFincaId: scope.fincaIds[0] ?? null,
      viewScope: scope.fincaIds.length ? 'finca' : 'all',
      selection: null,
      selectionSet: clearSelection ? emptySelectionSet() : state.selectionSet,
      genericSelection: selectionOutsideScope ? null : state.genericSelection,
      classificationConfig,
    });
  }
}
